import { request } from '@umijs/max';

// ============ Types ============

export interface AdminLoginRequest {
  username: string;
  password: string;
  autoLogin?: boolean;
}

export interface AdminLoginResponse {
  adminId: number;
  username: string;
  realName: string;
  role: string;
  permissions: string[];
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn?: number;
}

export interface AdminUser {
  adminId: number;
  username: string;
  realName: string;
  role: string;
  permissions: string[];
}

// ============ API Calls ============

export const adminLogin = async (data: AdminLoginRequest) => {
  return request<API.ApiResponse<AdminLoginResponse>>('/api/admin/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  });
};

export const refreshAdminToken = async (refreshToken: string) => {
  return request<API.ApiResponse<AdminLoginResponse>>('/api/admin/auth/refresh', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${refreshToken}`,
    },
    skipErrorHandler: true,
  });
};

// ============ Token Management ============

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_REFRESH_TOKEN_KEY = 'admin_refresh_token';
const ADMIN_USER_KEY = 'admin_user';

export const getAdminToken = (): string | null => {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
};

export const getAdminRefreshToken = (): string | null => {
  return localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
};

export const setAdminToken = (token: string, refreshToken: string) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken);
};

export const clearAdminToken = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
};

export const getAdminUser = (): AdminUser | null => {
  const user = localStorage.getItem(ADMIN_USER_KEY);
  if (!user) return null;
  try {
    return JSON.parse(user) as AdminUser;
  } catch {
    return null;
  }
};

export const setAdminUser = (user: AdminUser) => {
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
};

// ============ Permission Check ============

export const checkAdminPermission = (
  requiredPermissions: string | string[],
  userPermissions: string[],
  checkType: 'ANY' | 'ALL' = 'ANY'
): boolean => {
  const perms = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

  if (checkType === 'ANY') {
    return perms.some((perm) => userPermissions.includes(perm));
  } else {
    return perms.every((perm) => userPermissions.includes(perm));
  }
};
